/**
 * SegmentFillUnified — u6-segment-fill.
 *
 * ONE "Segment Fill" section for the segment side pane: the color/design of the
 * Segment FRAME. It folds the previously-separate stand-alone "Segment Color"
 * accordion together with the fill-type editor into a single accordion whose
 * type tabs are Color / Gradient / Pattern / Image / GIF — exactly the Edit-
 * Wheel-Flow deck (design-handoff `ewflow-segment-fills`, states 75x).
 *
 * RE-HOUSING, NOT FORKING: it mounts the EXISTING controls unchanged —
 *   · Color   → the app's rich <SegmentColor> palette (Preset / Custom / Wheel /
 *               Segment), injected into the panel's Color tab via the additive
 *               `colorPanel` prop. Writes the legacy `settings.color` field via
 *               the same useSegmentSettings().handleChange("segmentColor") path
 *               the stand-alone section used — the wedge colour behaviour is
 *               byte-for-byte the same.
 *   · Gradient/Pattern/Image/GIF → the WL-012 <SegmentFillPanel> pickers +
 *               FocalZoomControl and <GradientBuilderPanel>, writing the
 *               `settings.fill` model (+ the legacy currentBg/bgWallpaper fields
 *               via legacyFieldsForFill, + the snake_case model fields when
 *               SEGMENT_ARC_FILLS is ON) through the same undoable
 *               updateSegmentSetting dispatch SegmentStylingPane uses.
 *
 * No data model changes: this is presentation only. The Wallpaper section (the
 * open-Segment CANVAS background) is a separate section and is untouched.
 *
 * Gated by SEGMENT_FILL_UNIFIED_ENABLED at the call site (SegmentSettings): OFF
 * renders nothing here and the stand-alone "Segment Color" accordion shows as
 * today; ON renders this section and hides the stand-alone one.
 */

import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateSegmentSetting } from "../../../../redux/reducers/segmentBuilder";
import { withHistory } from "../../../../redux/actions/history";
import { getSelectedSegment } from "../../../../redux/selectors/segmentBuilderSelectors";
import { SEGMENT_ARC_FILLS_ENABLED } from "../../../D3Wheels/segmentArcFillsFlag";
import { defaultSegmentFill } from "../SegmentStyling/logic/segmentFill";
import { legacyFieldsForFill } from "../SegmentStyling/logic/legacyFillFields";
import { fillToModelFields, resolveSegmentFill } from "../SegmentStyling/logic/fillModelFeed";
import SegmentFillPanel from "../SegmentStyling/SegmentFillPanel";
import GradientBuilderPanel from "../SegmentStyling/GradientBuilderPanel";
import SegmentColor from "../../../SegmentSettings/children/SegmentColor";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ToolAccordion from "../ToolAccordion";
import useSegmentSettings from "../../../../hooks/useSegmentSetting";
import { useSegmentPalettes } from "../../../../hooks/useSegmentPalettes";
// Part A (flag REACT_APP_SEGMENT_PALETTE_COLOR): when a non-color fill is
// committed, (re)generate the Segment Palette FROM THE FILL so the Segment Color
// can harmonize with it. Flag OFF -> untouched.
import { SEGMENT_PALETTE_COLOR_ENABLED } from "../SegmentStyling/logic/segmentPaletteColorFlag";
// D18 (flag REACT_APP_SEG_PALETTE_AUTODERIVE): also re-derive the Segment Palette
// from the fill media, debounced, with an Undo-toast safety net.
import { SEG_PALETTE_AUTODERIVE } from "../../../../constants/featureFlags";

const SegmentFillUnified = ({ segment: segmentProp } = {}) => {
  const dispatch = useDispatch();
  const selected = useSelector(getSelectedSegment);
  const segment = segmentProp || selected;
  const { handleChange } = useSegmentSettings();
  const {
    fetchSegmentPalette,
    fetchSegmentPaletteFromFill,
    fetchSegmentPaletteFromFillDebounced,
  } = useSegmentPalettes();

  if (!segment) return null;

  // The `settings.fill` model (WL-012) — present only while the in-memory
  // redux state still holds whatever the user just picked THIS session.
  //
  // stgfix-1008-NEW-EditWheel-3 (image-fill-opens-color-tab, live-reproduced
  // wheel 507615 seg 6871694): `ActivitySettings` (yuga_wheel/models.py) has NO
  // `fill` column at all — only the LEGACY render fields (`current_bg`,
  // `bg_wallpaper`, `segment_bg_color`) actually persist. So after a reload,
  // `GET /api/v2/wheel/<id>/` NEVER returns a `settings.fill` key for ANY
  // segment — measured live: an Image fill saved, reloaded, still paints
  // correctly on the wedge (current_bg=2, bg_wallpaper=<url>), but this read
  // fell straight through to `defaultSegmentFill(segment.color)` (type
  // "color"), so the panel opened on the Color tab, the Image tab showed an
  // empty picker, and clicking it flagged "Unsaved changes" over a fill the
  // user never touched. `resolveSegmentFill` (fillModelFeed.js) already
  // reconstructs an image/gif/pattern model from those same legacy fields for
  // the WEDGE renderer (SegmentView, WheelComponent) — this panel just never
  // called it. Order matters: an in-session `settings.fill` (any type,
  // including gradient/color, which the resolver does not reconstruct) still
  // wins first, so live editing behaviour is unchanged; the resolver only
  // fills the gap once that blob is absent.
  const fill =
    (segment.settings && segment.settings.fill) ||
    resolveSegmentFill(segment.settings) ||
    defaultSegmentFill(segment.color);

  // Keep the panel's small live preview honest for the Color tab: show the
  // segment's actual wedge colour (which the injected <SegmentColor> edits)
  // rather than a stale/default model colour. No model write — display only.
  const displayFill =
    fill.type === "color"
      ? { ...fill, color: segment.color || fill.color }
      : fill;

  // ONE undoable dispatch: the V2 fill blob + the legacy render fields
  // (image/gif/pattern) + the additive snake_case model fields when the arc-
  // fills flag is ON. Identical to SegmentStylingPane.commitFill (reuse, not
  // fork) — color/gradient contribute no legacy fields, so those paths are
  // byte-identical.
  const commitFill = (nextFill) => {
    const data = {
      fill: nextFill,
      ...legacyFieldsForFill(nextFill),
      ...(SEGMENT_ARC_FILLS_ENABLED ? fillToModelFields(nextFill) : {}),
    };
    dispatch(withHistory(updateSegmentSetting({ id: segment.id, data })));
    // Part A: re-seed the Segment Palette FROM the newly-committed fill (image/
    // gif via the Palette Generator extractor; gradient/pattern via their
    // colors). Color fills route back to the color path inside the hook. Flag
    // OFF -> not called.
    if (
      (SEGMENT_PALETTE_COLOR_ENABLED || SEG_PALETTE_AUTODERIVE) &&
      segment.id != null
    ) {
      const nextSettings = { ...(segment.settings || {}), ...data };
      // D18 on -> debounced (collapse rapid media swaps) + Undo-toast safety net,
      // inside the hook. D18 off -> the original immediate call, byte-identical.
      if (SEG_PALETTE_AUTODERIVE) {
        fetchSegmentPaletteFromFillDebounced(nextSettings, segment.id);
      } else {
        fetchSegmentPaletteFromFill(nextSettings, segment.id);
      }
    }
  };

  const commitGradient = (nextGradient) =>
    commitFill({ ...fill, type: "gradient", gradient: nextGradient });

  // The Color tab re-houses the stand-alone "Segment Color" control verbatim:
  // write the legacy `settings.color` field + refresh the derived palette,
  // exactly as SegmentSettings did before this merge.
  const onSegmentColor = (color) => {
    handleChange("segmentColor", null, color);
    if (segment.id != null) fetchSegmentPalette(color, segment.id);
  };

  return (
    <ToolAccordion
      title="Segment Fill"
      hideDivider
      RightIconComponent={ExpandMoreIcon}
    >
      <SegmentFillPanel
        fill={displayFill}
        onChange={commitFill}
        segmentName={segment.title || segment.name || "Segment"}
        colorPanel={
          <SegmentColor
            showTabs={false}
            showSuggestedColorPalette={false}
            currentColor={segment.color}
            onColorChange={onSegmentColor}
          />
        }
      />
      {fill.type === "gradient" && (
        <GradientBuilderPanel
          gradient={fill.gradient}
          onChange={commitGradient}
        />
      )}
    </ToolAccordion>
  );
};

export default SegmentFillUnified;
